import { ActivationStrategy, FirebaseRemoteConfigClient } from '@softgames/firebase-remote-config-client';
import type { AppRemoteConfig, PlatformRemoteConfig, RawAppRemoteConfig } from '../../Platform';
import { getFirebaseAppOrNull, initFirebase } from './initFirebase';
import { DEFAULT_RAW_REMOTE_CONFIG, DEFAULT_REMOTE_CONFIG } from '../../../firebase/defaultRemoteConfig';
import { rawAppRemoteToAppRemote } from '../../../firebase/rawAppRemoteToAppRemote';

const fbTournamentUrl =
  'https://firebasestorage.googleapis.com/v0/b/hexa-stack-production.appspot.com/o/global-tournament.json?alt=media';

export class FacebookRemoteConfig implements PlatformRemoteConfig {
  private cachedRemoteConfig: AppRemoteConfig | null = null;

  private readonly defaultRemoteConfig: AppRemoteConfig = DEFAULT_REMOTE_CONFIG;

  constructor() {
    DEFAULT_RAW_REMOTE_CONFIG.feature_tournament_configUrl = fbTournamentUrl;
    DEFAULT_REMOTE_CONFIG.feature.tournament.configUrl = fbTournamentUrl;
  }

  public async init(): Promise<void> {
    if (this.cachedRemoteConfig) {
      return;
    }

    const isDev = ENVIRONMENT === 'dev';

    await initFirebase();

    const app = getFirebaseAppOrNull();
    if (!app) {
      return;
    }

    const client = new FirebaseRemoteConfigClient({
      getFirebaseApp: () => app,
      strategy: ActivationStrategy.FetchAndActivate,
      defaultConfig: mapDefaultsToFirebaseRC(DEFAULT_RAW_REMOTE_CONFIG),
      minFetchIntervalMS: isDev ? 1 : 43_200_000,
    });

    await client.init();
    this.parseRemoteConfig(client);
  }

  public getRemoteConfig(): AppRemoteConfig {
    return this.cachedRemoteConfig ?? this.defaultRemoteConfig;
  }

  private parseRemoteConfig(client: FirebaseRemoteConfigClient): void {
    this.cachedRemoteConfig = rawAppRemoteToAppRemote(
      parseRemoteConfigWithDefaults(client, DEFAULT_RAW_REMOTE_CONFIG),
      this.defaultRemoteConfig,
    );
  }
}

type FirebaseRemoteConfigData = Record<string, string | number | boolean>;

export function mapDefaultsToFirebaseRC(defaults: RawAppRemoteConfig): FirebaseRemoteConfigData {
  return { ...defaults };
}

export function parseRemoteConfigWithDefaults<T extends { [K in keyof T]: unknown }>(
  client: FirebaseRemoteConfigClient,
  defaults: T,
): T {
  const result = { ...defaults };

  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    const defaultValue = defaults[key];
    let parsed: unknown;

    if (typeof defaultValue === 'boolean') {
      parsed = client.getBoolean(key as string);
    } else if (typeof defaultValue === 'number') {
      parsed = client.getNumber(key as string);
    } else if (typeof defaultValue === 'string') {
      parsed = client.getString(key as string);
    } else {
      parsed = defaultValue;
    }

    result[key] = parsed as T[keyof T];
  });

  return result;
}
